import React, { useEffect, useRef, useState } from 'react';
import { CheckCheck, RefreshCw } from 'lucide-react';
import OperandiProcessVisibility from './OperandiProcessVisibility';
import { apiBase } from '../utils/authHeaders';
import { commandId } from './CkOperationsShared';
import { clearPendingMappingReview, loadPendingMappingReview, persistPendingMappingReview } from './operandiWorkflowQueue';

export default function OperandiProjectionHistory(props) {
  const { runId, scope, headers } = props;
  const identity = JSON.stringify([runId, scope?.orgId, scope?.locationId, scope?.token, headers?.Authorization, headers?.['X-Location-Id']]);
  return <ScopedProjectionHistory key={identity} {...props} />;
}

function ScopedProjectionHistory({ runId, scope, headers, snapshotId, onRecorded, onPendingChange }) {
  const [items, setItems] = useState([]);
  const [revision, setRevision] = useState(0);
  const [decision, setDecision] = useState('CHANGES_REQUIRED');
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [recovering, setRecovering] = useState(true);
  const submitting = useRef(false);
  const { orgId, locationId, token } = scope || {};
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let cancelled = false; setRecovering(true);
    loadPendingMappingReview({ orgId, locationId, token }, runId).then(saved => {
      if (cancelled) return;
      if (saved) { setPending(saved.request); setReason(saved.request.statement); setDecision(saved.request.decision);
        setMessage('Pending mapping review recovered. Outcome not yet confirmed.'); }
      setRecovering(false);
    });
    return () => { cancelled = true; };
  }, [orgId, locationId, token, runId]);
  useEffect(() => {
    if (!snapshotId) return undefined;
    const controller = new AbortController();
    setItems([]);
    fetch(`${apiBase()}/api/operandi/discovery-runs/${encodeURIComponent(runId)}/projection-history`,
      { headers, signal: controller.signal }).then(async response => {
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || 'Could not load projection history.');
      if (!controller.signal.aborted) setItems(payload.items || []);
    }).catch(e => { if (!controller.signal.aborted) setError(e.message); });
    return () => controller.abort();
  }, [runId, headers, snapshotId, revision]);
  useEffect(() => { onPendingChange?.(Boolean(pending) || recovering); }, [pending, recovering, onPendingChange]);
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);
  const current = items.find(item => item.id === snapshotId);
  const submit = async event => {
    event.preventDefault();
    if (submitting.current || recovering || (!pending && !current?.can_review)) return;
    submitting.current = true;
    const request = pending || { command_id: commandId(), action: 'review_mapping',
      thread_id: current.id, expected_version: current.review_version, expected_input_hash: current.input_hash,
      decision, statement: reason.trim(), evidence_refs: [] };
    setPending(request); setBusy(true); setError('');
    try {
      const saved = await persistPendingMappingReview(request, { orgId, locationId, token }, runId, () => mounted.current);
      if (!mounted.current) return;
      if (!saved) setMessage('Tab storage unavailable; keep this page open until the outcome is confirmed.');
      const response = await fetch(`${apiBase()}/api/operandi/discovery-runs/${encodeURIComponent(runId)}/workflow`,
        { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(request) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || 'Review outcome unknown; retry the same review.');
      if (payload.no_action_authority !== true || typeof payload.id !== 'string' || !Number.isInteger(payload.version))
        throw new Error('Invalid review acknowledgement; retry the same review.');
      if (mounted.current) { clearPendingMappingReview(request.command_id); setPending(null); setReason(''); setMessage('Mapping review recorded; no operational authority granted.'); setRevision(value => value + 1); onRecorded?.(); }
    } catch (e) { if (mounted.current) setError(e.message); }
    finally { submitting.current = false; if (mounted.current) setBusy(false); }
  };
  return <section className="mt-6 border-t border-gray-200 pt-4">
    <h2 className="font-semibold">Projection history and mapping review</h2>
    {error && <p role="alert" className="mt-2 text-sm text-red-800">{error}</p>}
    {message && <p role="status" className="mt-2 text-sm text-gray-700">{message}</p>}
    {current && <p className="mt-2 text-sm">Revision {current.revision}: {current.mapping_review}. Mapping review does not authorize operational actions.</p>}
    {(current?.can_review || pending) && <form onSubmit={submit} noValidate={!!pending} className="mt-3 space-y-2">
      <label className="block text-sm">Mapping decision<select value={decision} disabled={Boolean(pending)} onChange={e => setDecision(e.target.value)} className="ml-2 min-h-11 rounded border border-gray-300 px-2">
        <option value="CHANGES_REQUIRED">Changes required</option><option value="VALIDATED">Validated</option><option value="DISPUTED">Disputed</option>
      </select></label>
      <label className="block text-sm">Review evidence and reasoning<textarea required maxLength={2000} value={reason} disabled={Boolean(pending)} onChange={e => setReason(e.target.value)} className="mt-1 block min-h-24 w-full rounded border border-gray-300 p-2" /></label>
      <button disabled={busy || recovering || (!pending && !reason.trim())} className="inline-flex min-h-11 items-center gap-2 rounded border border-gray-300 px-3 text-sm">{pending ? <RefreshCw size={16} /> : <CheckCheck size={16} />}{pending ? 'Retry same review' : 'Record mapping review'}</button>
      {pending && <button type="button" disabled={busy} onClick={() => { clearPendingMappingReview(pending.command_id); setPending(null); setMessage('Retry discarded; this does not cancel an already accepted review.'); }} className="ml-3 min-h-11 text-sm underline">Discard pending retry</button>}
    </form>}
    {items.map(item => <details key={item.id} className="mt-3 border-t border-gray-200 pt-2">
      <summary className="cursor-pointer text-sm">Revision {item.revision} · {item.mapping_review}{item.id === snapshotId ? ' · Current' : ''}</summary>
      <p className="mt-2 break-all text-xs text-gray-600">Input hash: {item.input_hash}</p>
      {item.supersedes_id && <p className="break-all text-xs text-gray-600">Supersedes: {item.supersedes_id}</p>}
      {item.review_reason && <p className="mt-2 whitespace-pre-wrap text-sm">Review: {item.review_reason}</p>}
      <OperandiProcessVisibility projection={item.projection} />
    </details>)}
  </section>;
}
